import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';

// Styled Components
const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const NewsPageContainer = styled.div`
    padding: 2rem;
    background: linear-gradient(135deg, #1a1a1a, #2a2a2a);
    min-height: 100vh;
    color: #f1f1f1;
`;

const Title = styled.h1`
    text-align: center;
    color: #00e6e6;
    font-size: 2.5rem;
    margin-bottom: 2rem;
    text-shadow: 0 0 10px rgba(0, 230, 230, 0.5);

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const ErrorMessage = styled.div`
    text-align: center;
    color: #ff6b6b;
    font-size: 1.2rem;
    padding: 2rem;
    background: rgba(255, 107, 107, 0.1);
    border-radius: 10px;
    margin: 2rem auto;
    max-width: 600px;
`;

const LoadingContainer = styled.div`
    text-align: center;
    padding: 3rem;
`;

const Spinner = styled.div`
    width: 50px;
    height: 50px;
    border: 3px solid rgba(0, 230, 230, 0.3);
    border-radius: 50%;
    border-top-color: #00e6e6;
    animation: ${spin} 1s linear infinite;
    margin: 0 auto 1rem;
`;

const NewsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 2rem;
    padding: 1rem;
    max-width: 1400px;
    margin: 0 auto;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const NewsCard = styled.div`
    background: rgba(255, 255, 255, 0.05);
    border-radius: 15px;
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 230, 230, 0.1);
    height: 100%;
    display: flex;
    flex-direction: column;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 20px rgba(0, 230, 230, 0.2);
    }
`;

const ImageContainer = styled.div`
    width: 100%;
    height: 200px;
    overflow: hidden;
`;

const NewsImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;

    ${NewsCard}:hover & {
        transform: scale(1.05);
    }
`;

const NewsContent = styled.div`
    padding: 1.5rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
`;

const NewsTitle = styled.h2`
    font-size: 1.2rem;
    margin-bottom: 1rem;
    color: #00e6e6;
    line-height: 1.4;
`;

const NewsDescription = styled.p`
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    color: #cccccc;
    flex-grow: 1;
`;

const NewsMeta = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.8rem;
    color: #888;
    margin-top: auto;
    flex-wrap: wrap;
    gap: 1rem;

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const ReadMoreLink = styled.a`
    color: #00e6e6;
    text-decoration: none;
    padding: 0.5rem 1rem;
    border: 1px solid #00e6e6;
    border-radius: 20px;
    transition: all 0.3s ease;
    font-weight: 500;
    white-space: nowrap;

    &:hover {
        background: #00e6e6;
        color: #1a1a1a;
    }
`;

const AiNewsPage = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const API_KEY = process.env.REACT_APP_GNEWS_API_KEY;
                if (!API_KEY) {
                    setError('API key is not configured.');
                    setLoading(false);
                    return;
                }

                const response = await axios.get('https://gnews.io/api/v4/search', {
                    params: {
                        q: 'artificial intelligence',
                        lang: 'en',
                        country: 'us',
                        max: 10,
                        apikey: API_KEY
                    }
                });
                
                if (response.data && response.data.articles && response.data.articles.length > 0) {
                    const transformedNews = response.data.articles.map(article => ({
                        title: article.title || 'No Title',
                        description: article.description || 'No description available',
                        url: article.url,
                        image: { 
                            url: article.image || 'https://via.placeholder.com/350x200?text=AI+News' 
                        },
                        datePublished: article.publishedAt,
                        source: article.source.name
                    }));
                    
                    setNews(transformedNews);
                    setError(null);
                } else {
                    setError('No news articles found. Please try again later.');
                }
            } catch (error) {
                console.error('Error fetching news:', error);
                if (error.response) {
                    switch (error.response.status) {
                        case 401:
                            setError('Invalid API key. Please check the configuration.');
                            break;
                        case 403:
                            setError('API key is invalid or expired.');
                            break;
                        case 429:
                            setError('Daily API limit reached. Please try again tomorrow.');
                            break;
                        default:
                            setError(`Failed to fetch news: ${error.response?.data?.errors?.[0] || 'Unknown error'}`);
                    }
                } else if (error.request) {
                    setError('Network error. Please check your internet connection.');
                } else {
                    setError('An unexpected error occurred. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchNews();
        // Fetch news every hour to stay within API limits
        const interval = setInterval(fetchNews, 3600000);
        return () => clearInterval(interval);
    }, []);

    if (error) {
        return (
            <NewsPageContainer>
                <Title>Latest AI News</Title>
                <ErrorMessage>{error}</ErrorMessage>
            </NewsPageContainer>
        );
    }

    return (
        <NewsPageContainer>
            <Title>Latest AI News</Title>
            {loading ? (
                <LoadingContainer>
                    <Spinner />
                    <p>Fetching latest AI news...</p>
                </LoadingContainer>
            ) : (
                <NewsGrid>
                    {news.map((article, index) => (
                        <NewsCard key={index}>
                            {article.image && article.image.url && (
                                <ImageContainer>
                                    <NewsImage src={article.image.url} alt={article.title} />
                                </ImageContainer>
                            )}
                            <NewsContent>
                                <NewsTitle>{article.title}</NewsTitle>
                                <NewsDescription>{article.description}</NewsDescription>
                                <NewsMeta>
                                    <span>{new Date(article.datePublished).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}</span>
                                    <ReadMoreLink 
                                        href={article.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                    >
                                        Read More
                                    </ReadMoreLink>
                                </NewsMeta>
                            </NewsContent>
                        </NewsCard>
                    ))}
                </NewsGrid>
            )}
        </NewsPageContainer>
    );
};

export default AiNewsPage; 